import React, { useState, useEffect } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Input } from './ui/input';
import { MaskedInput } from './ui/masked-input';
import { Button } from './ui/button';
import { Autocomplete } from './ui/autocomplete';
import FilterDrawer, { FilterCount } from './FilterDrawer';

const isFilled = (value) => value !== '' && value !== null && value !== undefined;

/**
 * TableFilterComponent - Busca por texto + filtros em drawer
 *
 * @param {Array} filters - Filtros do drawer: [{ key, label, type: 'select'|'autocomplete'|'text'|'number'|'date', options, placeholder }]
 * @param {Object} filterValues - Filtros aplicados
 * @param {string} searchValue - Busca aplicada
 * @param {string} searchPlaceholder - Placeholder da busca
 * @param {Function} onApply - (search, filters) => void
 * @param {Function} onClear - Limpa busca e filtros
 */
function TableFilterComponent({
    filters = [],
    filterValues = {},
    searchValue = '',
    searchPlaceholder = 'Buscar...',
    onApply,
    onClear,
}) {
    const [open, setOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState(filterValues);
    const [localSearch, setLocalSearch] = useState(searchValue);

    useEffect(() => {
        setLocalSearch(searchValue);
    }, [searchValue]);

    useEffect(() => {
        if (!open) setLocalFilters(filterValues);
    }, [filterValues, open]);

    const activeCount = filters.filter((filter) => isFilled(filterValues[filter.key])).length;

    const setLocalFilter = (key, value) => setLocalFilters((prev) => ({ ...prev, [key]: value }));

    const applyAll = () => {
        onApply(localSearch, localFilters);
        setOpen(false);
    };

    const clearAll = () => {
        setLocalFilters({});
        setLocalSearch('');
        onClear();
        setOpen(false);
    };

    const clearSearch = () => {
        setLocalSearch('');
        onApply('', filterValues);
    };

    const renderFilterInput = (filter) => {
        const value = localFilters[filter.key] ?? '';

        switch (filter.type) {
            case 'select':
                return (
                    <select
                        value={value}
                        onChange={(e) => setLocalFilter(filter.key, e.target.value)}
                        className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="">Todos</option>
                        {filter.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );
            case 'autocomplete':
                return (
                    <Autocomplete
                        className="w-full"
                        options={filter.options || []}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        value={value === '' ? null : value}
                        onChange={(next) => setLocalFilter(filter.key, next ?? '')}
                        placeholder="Todos"
                    />
                );
            case 'date':
                return (
                    <MaskedInput
                        mask="date"
                        value={value}
                        onChange={(e) => setLocalFilter(filter.key, e.target.value)}
                    />
                );
            case 'number':
                return (
                    <Input
                        type="number"
                        value={value}
                        onChange={(e) => setLocalFilter(filter.key, e.target.value)}
                        placeholder={filter.placeholder}
                    />
                );
            default:
                return (
                    <Input
                        type="text"
                        value={value}
                        onChange={(e) => setLocalFilter(filter.key, e.target.value)}
                        placeholder={filter.placeholder}
                    />
                );
        }
    };

    return (
        <>
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        className="pl-10 pr-10"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onApply(localSearch, filterValues);
                        }}
                    />
                    {localSearch !== '' && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="Limpar busca"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <Button type="button" className="h-10" onClick={() => onApply(localSearch, filterValues)}>
                    <Search className="w-4 h-4 mr-2" />
                    Buscar
                </Button>
                {filters.length > 0 && (
                    <Button
                        type="button"
                        variant="outline"
                        className="h-10 relative"
                        onClick={() => setOpen(true)}
                    >
                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                        Filtros
                        <FilterCount count={activeCount} />
                    </Button>
                )}
            </div>

            {filters.length > 0 && (
                <FilterDrawer open={open} onClose={() => setOpen(false)} onApply={applyAll} onClear={clearAll}>
                    {filters.map((filter) => (
                        <div key={filter.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {filter.label}
                            </label>
                            {renderFilterInput(filter)}
                        </div>
                    ))}
                </FilterDrawer>
            )}
        </>
    );
}

export default TableFilterComponent;
